import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { RevealStoredValue } from '@/components/ui/RevealStoredValue';
import {
  type SmtpEncryption,
  type SystemSmtpSettings,
  revealSystemSmtpPassword,
  useSystemSmtpSettings,
  useTestSystemSmtp,
  useUpdateSystemSmtpSettings,
} from '@/lib/smtp';

/**
 * Superuser-only panel for managing the site-wide mail server. Used for
 * password reset / verification emails (always system-routed) and as the
 * default sender for org-scoped emails like invitations when the org has
 * `use_system_credentials = true`.
 *
 * Single row globally, so the UI is a flat form rather than a list. The
 * separate per-org allowlist gates surface only when at least one org has
 * been revoked — otherwise we keep the page tidy.
 */
export function SystemSmtpSettingsPanel() {
  const settings = useSystemSmtpSettings();
  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold">Outbound mail (SMTP)</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Configures the shared mail server used for password reset and
        verification links, plus the default sender for organization
        emails (invitations) when an org opts in to shared credentials.
      </p>

      {settings.isPending && (
        <p className="mt-4 text-sm text-stone-500 dark:text-stone-400">Loading…</p>
      )}
      {settings.isError && (
        <p className="mt-4 text-sm text-red-600">
          {(settings.error as Error).message}
        </p>
      )}
      {settings.data && <SystemSmtpForm row={settings.data} />}
    </section>
  );
}

function SystemSmtpForm({ row }: { row: SystemSmtpSettings }) {
  const update = useUpdateSystemSmtpSettings();
  const test = useTestSystemSmtp();

  const [enabled, setEnabled] = useState(row.enabled);
  const [host, setHost] = useState(row.host ?? '');
  const [port, setPort] = useState(row.port);
  const [encryption, setEncryption] = useState<SmtpEncryption>(row.encryption);
  const [username, setUsername] = useState(row.username ?? '');
  const [password, setPassword] = useState('');
  const [fromAddress, setFromAddress] = useState(row.from_address ?? '');
  const [fromName, setFromName] = useState(row.from_name ?? '');
  const [showSuccess, setShowSuccess] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    setEnabled(row.enabled);
    setHost(row.host ?? '');
    setPort(row.port);
    setEncryption(row.encryption);
    setUsername(row.username ?? '');
    setPassword('');
    setFromAddress(row.from_address ?? '');
    setFromName(row.from_name ?? '');
  }, [
    row.enabled,
    row.host,
    row.port,
    row.encryption,
    row.username,
    row.has_password,
    row.from_address,
    row.from_name,
  ]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setShowSuccess(false);
    update.mutate(
      {
        enabled,
        host: host.trim() || null,
        port,
        encryption,
        username: username.trim() || null,
        // Empty string means "leave existing password alone". The clear
        // button below is the explicit way to null it.
        password: password ? password : null,
        from_address: fromAddress.trim() || null,
        from_name: fromName.trim() || null,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
          setPassword('');
        },
      },
    );
  };

  const onClearPassword = () => {
    if (!confirm('Clear the stored SMTP password?')) return;
    update.mutate({
      enabled,
      host: host.trim() || null,
      port,
      encryption,
      username: username.trim() || null,
      clear_password: true,
      from_address: fromAddress.trim() || null,
      from_name: fromName.trim() || null,
    });
  };

  const onTest = () => {
    if (!testTo.trim()) return;
    test.mutate(testTo.trim());
  };

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-4">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Enable site-wide outbound mail
      </label>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Host" hint="smtp.gmail.com, mail.example.com, ses.eu-west-1.amazonaws.com…">
          <Input
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="smtp.example.com"
          />
        </Field>
        <Field label="Port" hint="STARTTLS uses 587, SSL uses 465, plain typically 25">
          <Input
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => setPort(Number(e.target.value) || 587)}
          />
        </Field>

        <Field label="Encryption">
          <select
            value={encryption}
            onChange={(e) => setEncryption(e.target.value as SmtpEncryption)}
            className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
          >
            <option value="starttls">STARTTLS (port 587)</option>
            <option value="ssl">SSL/TLS (port 465)</option>
            <option value="none">None — plaintext</option>
          </select>
        </Field>
        <Field label="Username (optional)" hint="Leave blank if the relay doesn't require auth">
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="apikey, postmaster@…, etc."
            autoComplete="off"
          />
        </Field>
        <Field
          label="Password / API key"
          hint={
            row.has_password
              ? 'A password is stored. Leave blank to keep it; type a new one to replace.'
              : 'Required if the relay needs auth.'
          }
        >
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={row.has_password ? 'saved — leave blank to keep' : ''}
            autoComplete="off"
          />
          {row.has_password && (
            <>
              <RevealStoredValue
                fetcher={revealSystemSmtpPassword}
                onReveal={(v) => setPassword(v)}
                onHide={() => setPassword('')}
                label="stored password"
              />
              <button
                type="button"
                onClick={onClearPassword}
                className="mt-1 text-xs text-red-600 hover:underline"
              >
                Clear stored password
              </button>
            </>
          )}
        </Field>
        <Field label="From address" hint="What recipients see. Some relays reject mismatched senders.">
          <Input
            value={fromAddress}
            onChange={(e) => setFromAddress(e.target.value)}
            placeholder="noreply@yourdomain.com"
          />
        </Field>
        <Field label="From name (optional)" hint="Display name in the From header. e.g. PartForge">
          <Input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="PartForge"
          />
        </Field>
      </div>

      {row.last_error && (
        <p className="rounded border border-red-300 bg-red-50 dark:bg-red-900/30 p-2 text-xs text-red-800 dark:text-red-300">
          Last error: {row.last_error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save settings'}
        </Button>
        {update.isError && (
          <p className="text-sm text-red-600">{(update.error as Error).message}</p>
        )}
        {showSuccess && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</p>
        )}
      </div>

      <div className="border-t border-stone-200 dark:border-stone-800 pt-4">
        <h3 className="font-medium">Send a test email</h3>
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          Sends a real message through the saved credentials. Save first if
          you've made changes — the test uses what's stored, not the form's
          current values.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="recipient@example.com"
            className="max-w-xs"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={onTest}
            disabled={test.isPending || !testTo.trim()}
          >
            {test.isPending ? 'Sending…' : 'Send test'}
          </Button>
          {test.data?.ok === true && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              {test.data.detail}
            </p>
          )}
          {test.data?.ok === false && (
            <p className="text-sm text-red-600">{test.data.detail}</p>
          )}
          {test.isError && (
            <p className="text-sm text-red-600">{(test.error as Error).message}</p>
          )}
        </div>
      </div>

      {row.blocked_org_slugs.length > 0 && (
        <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-2 text-xs text-amber-900 dark:text-amber-200">
          <p className="font-medium">
            Organizations with shared-mail access revoked:
          </p>
          <ul className="mt-1 list-disc list-inside font-mono">
            {row.blocked_org_slugs.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          <p className="mt-1">
            Use the org-level allowlist endpoint to re-grant access:{' '}
            <code className="font-mono">
              PUT /api/admin/smtp-settings/orgs/&lt;slug&gt;/allowlist
            </code>
          </p>
        </div>
      )}
    </form>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-stone-700 dark:text-stone-300">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-xs text-stone-500 dark:text-stone-400">
          {hint}
        </span>
      )}
    </label>
  );
}
